<template>
  <v-container class="px-0">
    <!-- Tableau -->
    <v-card outlined v-if="displayMode === 'dataTable'">
      <v-data-table
        :headers="headers"
        :items="pagination.items"
        item-key="id"
        show-select
        :value="selectedRows"
        @input="onTableSelect"
        :item-class="(item) => (item.used ? 'used-row' : '')"
        disable-sort
        disable-filtering
        hide-default-footer
        :items-per-page="-1"
        class="elevation-1"
      >
        <template v-slot:item.data-table-select="{ item, isSelected, select }">
          <v-simple-checkbox
            :value="isSelected"
            :disabled="item.used"
            @input="select($event)"
          />
        </template>
        <template v-slot:item.title="{ item }">
          <a :href="item.url" target="_blank">{{ item.title }}</a>
        </template>
        <template v-slot:item.rubric_slug="{ item }">
          {{ rubricLabel(item.rubric_slug) }}
        </template>
        <template v-slot:item.created="{ item }">
          {{ formatDate(item.created) }}
        </template>
      </v-data-table>
    </v-card>

    <!-- Widget (masonry) -->
    <v-row v-else-if="displayMode === 'masonry'">
      <v-col cols="4" v-for="(column, index) in masonryState.columns" :key="index">
        <internal-sources-card
          v-for="item in column.items"
          :key="`editorial_${item.id}`"
          :item="item"
          :selected="isSelected(item.id)"
          @toggle-select="toggleSelect"
          @height-calculated="(height) => updateMasonryColumnHeight(index, height)"
        />
      </v-col>
    </v-row>

    <!-- Fil d'actualité -->
    <internal-sources-card
      v-else
      v-for="item in pagination.items"
      :key="`editorial_${item.id}`"
      :item="item"
      :selected="isSelected(item.id)"
      @toggle-select="toggleSelect"
    />

    <div ref="infiniteScrollSentinel" style="height: 1px"></div>

    <back-to-top />

    <div v-if="page.loading" class="text-center my-4">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <p
      v-else-if="pagination.total === 0 && page.searched"
      class="text-center my-6 text--secondary"
    >
      Aucun article pour ces filtres.
    </p>
  </v-container>
</template>

<script>
import { computed, reactive, watch } from "@vue/composition-api";

import BackToTop from "@/components/common/buttons/BackToTop.vue";
import InternalSourcesCard from "@/components/ai-factory/internal-sources/Card.vue";

import useAxios from "@/hooks/useAxios";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import useMasonry from "@/hooks/useMasonry";
import useProxy from "@/hooks/useProxy";

const RUBRIC_LABELS = {
  cuisine: "Cuisine",
  mode: "Mode",
  beaute: "Beauté",
  maison_deco: "Maison & Déco",
};

export default {
  name: "InternalSourcesResults",
  components: {
    BackToTop,
    InternalSourcesCard,
  },
  setup() {
    const { axiosGet } = useAxios();
    const { store } = useProxy();
    const { manageInfiniteScroll, sentinel } = useInfiniteScroll(async () => {
      pagination.options.page += 1;
      page.forceCall = true;
      await getData();
    });
    const {
      loadMasonryItems,
      masonryState,
      resetMasonry,
      updateMasonryColumnHeight,
    } = useMasonry(3);

    const page = reactive({
      loading: false,
      forceCall: false,
      searched: false,
    });

    const pagination = reactive({
      total: 0,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 25,
      },
    });

    const headers = [
      { text: "Site", value: "site_name" },
      { text: "Rubrique", value: "rubric_slug" },
      { text: "Catégorie", value: "category_name" },
      { text: "Titre", value: "title" },
      { text: "Date", value: "created" },
    ];

    // =>  COMPUTED
    const applyFilters = computed(() => store.getters["common/getApplyFilters"]);
    const rubrics = computed(() => store.getters["editorial/getRubrics"]);
    const siteIds = computed(() => store.getters["editorial/getSiteIds"]);
    const dates = computed(() => store.getters["editorial/getDates"]);
    const displayMode = computed(() => store.getters["editorial/getDisplayMode"]);
    const selectedRows = computed(() => store.getters["editorial/getSelected"]);

    // =>  METHODS
    const isSelected = (id) => store.getters["editorial/isSelected"](id);
    const toggleSelect = (item) => store.dispatch("editorial/toggleSelect", item);
    const onTableSelect = (rows) =>
      store.commit(
        "editorial/setSelected",
        Object.fromEntries(rows.filter((r) => !r.used).map((r) => [r.id, r]))
      );
    const rubricLabel = (slug) => RUBRIC_LABELS[slug] || slug;
    const formatDate = (value) => {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    };

    const gotData = () => {
      if (applyFilters.value) {
        store.dispatch("common/updateApplyFilters", false);
      }
      page.forceCall = false;
    };

    const getData = async () => {
      if ((applyFilters.value || page.forceCall) && !page.loading) {
        try {
          page.loading = true;
          const params = {
            page: pagination.options.page,
            limit: pagination.options.itemsPerPage,
            sort_desc: true,
          };
          if (rubrics.value && rubrics.value.length)
            params.rubrics = rubrics.value.join(",");
          if (siteIds.value && siteIds.value.length)
            params.site_ids = siteIds.value.join(",");
          if (dates.value && dates.value[0]) params.date_from = dates.value[0];
          if (dates.value && dates.value[1]) params.date_to = dates.value[1];

          const {
            data: { total, items },
          } = await axiosGet("/editorial/leparisien/feed", params);

          pagination.total = total;
          if (pagination.options.page === 1) {
            pagination.items = items;
          } else {
            pagination.items.push(...items);
          }
          if (displayMode.value === "masonry") {
            loadMasonryItems(items);
          }
          page.searched = true;
          manageInfiniteScroll(pagination.items.length, pagination.total);
        } catch (error) {
          console.error("error while fetching internal sources feed");
        }
        page.loading = false;
      }
      gotData();
    };

    // =>  WATCH
    watch(applyFilters, async (newValue) => {
      if (newValue) {
        store.dispatch("editorial/clearSelection");
        pagination.total = 0;
        pagination.items = [];
        pagination.options.page = 1;
        resetMasonry();
        await getData();
      }
    });

    watch(displayMode, () => {
      pagination.total = 0;
      pagination.items = [];
      pagination.options.page = 1;
      resetMasonry();
      page.forceCall = true;
      getData();
    });

    return {
      headers,
      displayMode,
      masonryState,
      page,
      pagination,
      selectedRows,
      infiniteScrollSentinel: sentinel,
      updateMasonryColumnHeight,
      isSelected,
      toggleSelect,
      onTableSelect,
      rubricLabel,
      formatDate,
    };
  },
};
</script>

<style scoped>
::v-deep .used-row {
  opacity: 0.55;
}
</style>
